import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AskCAACI = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check if chat is enabled
  useEffect(() => {
    const checkChatStatus = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setChatEnabled(response.data.chat_enabled !== false);
      } catch (error) {
        // If settings fail, assume chat is enabled
        setChatEnabled(true);
      } finally {
        setCheckingStatus(false);
      }
    };
    checkChatStatus();
  }, []);

  // Add welcome message when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0 && chatEnabled) {
      setMessages([{
        role: 'assistant',
        content: "Hello! I'm the CAACI Assistant. I can help you learn about AI research, our services, and answer your questions about artificial intelligence. How can I assist you today?"
      }]);
    }
  }, [isOpen, messages.length, chatEnabled]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading || !chatEnabled) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage
      });

      setSessionId(response.data.session_id);
      
      // Add AI response
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = error.response?.status === 503 
        ? "Chat is currently disabled. Please contact CAACI directly for assistance."
        : "I'm sorry, I encountered an error. Please try again or contact CAACI directly for assistance.";
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: errorMessage
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Don't render anything while checking status or if chat is disabled
  if (checkingStatus) return null;
  if (!chatEnabled) return null;

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-20 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-gray-800 hover:bg-gray-700' 
            : 'bg-blue-600 hover:bg-blue-700 animate-pulse hover:animate-none'
        }`}
        data-testid="ask-caaci-button"
        aria-label="Ask CAACI"
      >
        {isOpen ? (
          <X className="text-white" size={24} />
        ) : (
          <MessageCircle className="text-white" size={24} />
        )}
      </button>

      {/* Chat Widget */}
      {isOpen && (
        <div 
          className="fixed bottom-36 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
          style={{ height: '500px', maxHeight: 'calc(100vh - 200px)' }}
          data-testid="ask-caaci-widget"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Bot size={22} />
            </div>
            <div>
              <h3 className="font-semibold text-base">Ask CAACI</h3>
              <p className="text-xs text-blue-100">AI Research Assistant</p>
            </div>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {message.role === 'assistant' && (
                      <Bot size={16} className="mt-0.5 text-blue-600 flex-shrink-0" />
                    )}
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                    {message.role === 'user' && (
                      <User size={16} className="mt-0.5 flex-shrink-0" />
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin text-blue-600" />
                    <span className="text-sm text-gray-500">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 p-3 bg-white">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about AI research..."
                className="flex-1 rounded-full border-gray-300 focus:border-blue-500"
                disabled={isLoading}
                data-testid="ask-caaci-input"
              />
              <Button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                className="rounded-full w-10 h-10 p-0 bg-blue-600 hover:bg-blue-700"
                data-testid="ask-caaci-send"
              >
                <Send size={18} />
              </Button>
            </form>
            <p className="text-xs text-gray-400 text-center mt-2">
              Powered by CAACI AI
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AskCAACI;
